/**
 * NotificationBell — in-app notification bell for PortalShell AppBar (spec-11.1 W4)
 *
 * - Polls GET /api/v1/notifications/bell every 30s for unread count
 * - Badge shows unread count; click opens MUI Popover listing last 10
 * - AR-first: subject_ar preferred when available
 * - "Mark All Read" button calls PATCH /api/v1/notifications/read-all
 * - R7 mitigation: tenantId from user.tenant_id (available via useAuth)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

import { isRtl } from '../i18n/config';
import { useAuth } from '../auth/AuthProvider';
import axios from 'axios';

// ── Types ──────────────────────────────────────────────────────

interface BellNotification {
  id: string;
  subject: string;
  subject_ar?: string | null;
  body?: string | null;
  body_ar?: string | null;
  category: string;
  priority: string;
  read_at: string | null;
  deep_link?: string | null;
  created_at: string;
}

interface BellResponse {
  items: BellNotification[];
  unread_count: number;
}

// ── Constants ──────────────────────────────────────────────────

const PRIMARY_TENANT_ID = '11111111-1111-1111-1111-111111111111';

// ── API helpers ────────────────────────────────────────────────

async function fetchBell(userId: string, tenantId: string): Promise<BellResponse> {
  const resp = await axios.get('/api/v1/notifications/bell', {
    params: { user_id: userId, limit: 10 },
    headers: { 'X-Tenant-ID': tenantId },
  });
  // Accept { items, unread_count } or flat array
  if (Array.isArray(resp.data)) {
    const items = resp.data as BellNotification[];
    return { items, unread_count: items.filter((n) => !n.read_at).length };
  }
  return resp.data as BellResponse;
}

async function markAllRead(userId: string, tenantId: string): Promise<void> {
  await axios.patch('/api/v1/notifications/read-all', null, {
    params: { user_id: userId },
    headers: { 'X-Tenant-ID': tenantId },
  });
}

async function markOneRead(notificationId: string, tenantId: string): Promise<void> {
  await axios.patch(`/api/v1/notifications/${notificationId}/read`, null, {
    headers: { 'X-Tenant-ID': tenantId },
  });
}

// ── Component ──────────────────────────────────────────────────

export interface NotificationBellProps {
  /** Fallback user ID (normally comes from useAuth) */
  userId?: string;
  /** Fallback tenant ID (normally comes from user.tenant_id) */
  tenantId?: string;
}

export default function NotificationBell({ userId: userIdProp, tenantId: tenantIdProp }: NotificationBellProps) {
  const { t, i18n } = useTranslation('notifications');
  const rtl = isRtl(i18n.language);
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Resolve userId / tenantId with fallback chain
  const userId = user?.id ?? userIdProp ?? '';
  const tenantId = user?.tenant_id ?? tenantIdProp ?? PRIMARY_TENANT_ID;

  const { data, isLoading } = useQuery({
    queryKey: ['bell', userId],
    queryFn: () => fetchBell(userId, tenantId),
    enabled: !!userId,
    refetchInterval: 30_000, // poll every 30s
    staleTime: 15_000,
  });

  const markAllMutation = useMutation({
    mutationFn: () => markAllRead(userId, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bell', userId] });
    },
  });

  const markOneMutation = useMutation({
    mutationFn: (id: string) => markOneRead(id, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bell', userId] });
    },
  });

  const unreadCount = data?.unread_count ?? 0;
  const notifications = data?.items ?? [];

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (n: BellNotification) => {
    if (!n.read_at) {
      markOneMutation.mutate(n.id);
    }
    if (n.deep_link) {
      navigate(n.deep_link);
    }
    handleClose();
  };

  const popoverOpen = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={t('bell.unread', { count: unreadCount })}>
        <IconButton onClick={handleOpen} size="small" sx={{ color: '#4A5568' }}>
          <Badge
            badgeContent={unreadCount > 0 ? unreadCount : undefined}
            color="error"
            max={99}
          >
            {unreadCount > 0 ? (
              <NotificationsIcon sx={{ fontSize: 20 }} />
            ) : (
              <NotificationsNoneIcon sx={{ fontSize: 20 }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: rtl ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: rtl ? 'left' : 'right' }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            bgcolor: '#FFFFFF',
            border: '1px solid #E3E7EC',
            color: '#c9d4e0',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box
          dir={rtl ? 'rtl' : 'ltr'}
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #E3E7EC',
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0E1116' }}>
            {t('bell.unread', { count: unreadCount })}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={() => markAllMutation.mutate()}
              disabled={markAllMutation.isPending}
              sx={{ color: '#0B2545', fontSize: '0.75rem', textTransform: 'none', minWidth: 0 }}
            >
              {markAllMutation.isPending ? (
                <CircularProgress size={12} />
              ) : (
                t('bell.markAllRead')
              )}
            </Button>
          )}
        </Box>

        {/* Body */}
        <Box dir={rtl ? 'rtl' : 'ltr'} sx={{ overflowY: 'auto', flex: 1 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!isLoading && notifications.length === 0 && (
            <Typography
              variant="body2"
              sx={{ color: '#4A5568', textAlign: 'center', py: 4, px: 2 }}
            >
              {t('bell.noNotifications')}
            </Typography>
          )}
          {!isLoading && notifications.length > 0 && (
            <List dense disablePadding>
              {notifications.map((n, idx) => {
                const subject = rtl && n.subject_ar ? n.subject_ar : n.subject;
                const unread = !n.read_at;
                return (
                  <React.Fragment key={n.id}>
                    <ListItemButton
                      onClick={() => handleItemClick(n)}
                      sx={{
                        py: 1,
                        px: 2,
                        bgcolor: unread ? 'rgba(11, 37, 69, 0.06)' : 'transparent',
                        '&:hover': { bgcolor: '#F0F2F5' },
                        alignItems: 'flex-start',
                      }}
                    >
                      {unread && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: '#0B2545',
                            mt: 0.75,
                            mr: rtl ? 0 : 1,
                            ml: rtl ? 1 : 0,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <ListItemText
                        primary={subject}
                        secondary={new Date(n.created_at).toLocaleString()}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            color: unread ? '#0E1116' : '#4A5568',
                            fontWeight: unread ? 600 : 400,
                            fontSize: '0.82rem',
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: { color: '#5a7a99', fontSize: '0.72rem' },
                        }}
                      />
                    </ListItemButton>
                    {idx < notifications.length - 1 && (
                      <Divider sx={{ borderColor: '#E3E7EC' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Box
          dir={rtl ? 'rtl' : 'ltr'}
          sx={{
            px: 2,
            py: 1,
            borderTop: '1px solid #E3E7EC',
            flexShrink: 0,
          }}
        >
          <Button
            size="small"
            fullWidth
            onClick={() => {
              navigate('/me/notifications');
              handleClose();
            }}
            sx={{ color: '#0B2545', textTransform: 'none', fontSize: '0.75rem' }}
          >
            {t('bell.viewAll')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
